import React, { useState, useRef, useEffect } from 'react';
import type { KeyboardEvent, FocusEvent } from 'react';
import StationMarker from '@engineering/icons/station-marker';
import {
  getLocation,
  Runtime,
  useSearchSuggestions,
} from '@silvertours/front-entities';

import { LocationSuggest } from '../LocationSuggest';
import {
  SearchHistory,
  clearSearchHistory,
  getSearchHistory,
} from '../SearchHistory';
import type { SearchHistoryItem, SearchHistoryType } from '../SearchHistory';
import { StyledInput } from './SearchInput.styles';

type SearchInputProps = {
  deleteHistoryLabel: string;
  error?: string;
  fieldName: string;
  historyCallback?: (historyItem: SearchHistoryItem) => void;
  historyHeadline: string;
  label: string;
  noResultsText: string;
  showHistory?: boolean;
  suggestionCallback: (suggestion: any) => void;
  value: string;
};

const SearchInput = ({
  deleteHistoryLabel,
  error,
  fieldName,
  historyCallback,
  historyHeadline,
  label,
  noResultsText,
  showHistory = false,
  suggestionCallback,
  value,
}: SearchInputProps) => {
  const [showSearchHistory, setShowSearchHistory] = useState<boolean>(false);
  const [showLocationSuggest, setShowLocationSuggest] =
    useState<boolean>(false);

  const [searchHistory, setSearchHistory] = useState<SearchHistoryType>({
    items: [],
  });
  useEffect(() => {
    setSearchHistory(getSearchHistory());
  }, []);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const fieldRef = useRef<HTMLInputElement>(null);

  const [errorMessage, setErrorMessage] = useState<string | undefined>(error);

  const { language, market } = Runtime.useLocale();
  const { consumerApiSettings } = Runtime.useConsumerApi();

  const { clearResults, fetchSuggestions, loading, results } =
    useSearchSuggestions(language, market, consumerApiSettings);

  useEffect(() => {
    setErrorMessage(error);
  }, [error]);

  const handleChange = (inputValue: string) => {
    setSearchTerm(inputValue);
    if (inputValue === '') {
      clearResults();
      setShowSearchHistory(true);
      setShowLocationSuggest(false);
    } else {
      setShowLocationSuggest(true);
      setShowSearchHistory(false);
      fetchSuggestions(inputValue);
    }
    suggestionCallback({});
  };

  const handleSuggestionValue = (suggestion?: any) => {
    if (suggestion) {
      const name = `${suggestion.cityName} ${
        suggestion.typeDisplay ? suggestion.typeDisplay : ''
      }`;
      setSearchTerm(name);

      setShowLocationSuggest(false);
      clearResults();
      suggestionCallback(getLocation(suggestion));
    } else {
      handleChange(value);
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    handleEnterKey?: () => void,
  ) => {
    switch (event.key) {
      case 'Enter':
        if (handleEnterKey) {
          event.preventDefault();
          handleEnterKey();
          setShowSearchHistory(false);
          setShowLocationSuggest(false);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShowSearchHistory(false);
        setShowLocationSuggest(false);
        break;
      default:
        break;
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      setShowSearchHistory(true);
    } else {
      fetchSuggestions(event.target.value);
      setShowLocationSuggest(true);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (!event.relatedTarget?.className.includes('searchHistory')) {
      setShowSearchHistory(false);
    }
    if (
      !(
        event.relatedTarget?.className.includes('inputCloseIcon') ||
        event.relatedTarget?.className.includes('locationSuggest')
      )
    ) {
      setShowLocationSuggest(false);
    }
  };

  return (
    <>
      <StyledInput
        ref={fieldRef}
        id={fieldName}
        type="text"
        label={label}
        value={value || searchTerm}
        error={errorMessage}
        autoComplete="off"
        icon={<StationMarker />}
        iconPosition="left"
        appearActive
        hiddenLabel
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={event => {
          handleChange(event.target.value);
        }}
      />
      {showHistory && (
        <SearchHistory
          hidden={searchHistory.items?.length === 0 || !showSearchHistory}
          history={searchHistory}
          handleItemClick={(item: SearchHistoryItem) => {
            if (historyCallback) {
              historyCallback(item);
            }
            setShowSearchHistory(false);
          }}
          handleKeyDown={handleKeyDown}
          clearSearchHistoryCallback={() => {
            clearSearchHistory();
            setSearchHistory(getSearchHistory());
            setShowSearchHistory(false);
          }}
          onLastElementBlur={() => {
            setShowSearchHistory(false);
          }}
          headline={historyHeadline}
          deleteLabel={deleteHistoryLabel}
        />
      )}
      {showLocationSuggest && (
        <LocationSuggest
          connectedInputContainer={fieldRef}
          onItemClick={handleSuggestionValue}
          onLastElementBlur={() => {
            setShowLocationSuggest(false);
          }}
          results={results}
          noResultsText={noResultsText}
          loading={loading}
        />
      )}
    </>
  );
};

export { SearchInput };
